import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import QRCode from "qrcode";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import AvailabilityCalendar from "../AvailabilityCalendar";
import { toast } from "../../lib/toast";

import BookingSuccessScreen from "./BookingSuccessScreen";
import SchedulePicker from "./SchedulePicker";
import ContactFields from "./ContactFields";
import RequirementsSection from "./RequirementsSection";
import ScrollHint from "./ScrollHint";
import GuidedFooter, { bookingStepStatus, BOOKING_STEPS } from "./GuidedFooter";
import StepDots, { scrollToSection } from "./StepDots";
import PriorityPicker, { PRIORITY_TIERS } from "../customer/PriorityPicker";
import usePriorityConfig from "../../hooks/usePriorityConfig";
import { logger } from "../../utils/logger";


const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helpers — kept module-local so we don't pollute the booking namespace
const isDateAllowed = (dateString) => {
  const [year, month, day] = dateString.split("-").map(Number);
  const dayOfWeek = new Date(year, month - 1, day).getDay();
  return dayOfWeek >= 1 && dayOfWeek <= 4; // Mon-Thu
};

const buildMissingFields = (bookingData) => {
  const errors = {};
  const missing = [];
  const required = [
    ["pickup_date", "Pickup Date", (v) => !v],
    ["pickup_time", "Pickup Time", (v) => !v],
    ["address", "Service Address", (v) => !v || v.trim() === ""],
    ["phone", "Phone Number", (v) => !v || v.trim() === ""],
    ["email", "Email Address", (v) => !v || v.trim() === ""],
    ["curbside_confirmed", "Curbside Confirmation", (v) => !v],
  ];
  required.forEach(([key, label, isMissing]) => {
    if (isMissing(bookingData[key])) {
      errors[key] = true;
      missing.push(label);
    }
  });
  return { errors, missing };
};

const BookingModal = ({ quote, onClose, onSuccess, onVenmoPayment, priorityTier, onPriorityChange }) => {
  const [bookingData, setBookingData] = useState({
    pickup_date: "",
    pickup_time: "",
    address: "",
    phone: "",
    email: "",
    special_instructions: "",
    curbside_confirmed: false,
    email_notifications: true,
  });
  const [bookingSubmitted, setBookingSubmitted] = useState(false);
  const [bookedTimeSlots, setBookedTimeSlots] = useState([]);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [legalConsent, setLegalConsent] = useState(false);
  const [payInPerson, setPayInPerson] = useState(false);

  // Dynamic priority fees (admin-configurable). Falls back to PRIORITY_TIERS values.
  const { fees: priorityFees } = usePriorityConfig();
  const priorityFeeAmount = priorityTier
    ? (priorityFees?.[priorityTier] ?? PRIORITY_TIERS.find(t => t.id === priorityTier)?.fee ?? 0)
    : 0;
  const equipmentFeeAmount = quote.equipment_required ? (quote.equipment_fee || 0) : 0;
  // Route-based all-in price (computed server-side once the address is entered).
  // null = not yet computed / disabled → fall back to the AI price + add-ons.
  const [travelQuote, setTravelQuote] = useState(null);
  const [travelLoading, setTravelLoading] = useState(false);
  const bodyScrollRef = useRef(null);
  const stepStatus = bookingStepStatus(bookingData);
  const formComplete = BOOKING_STEPS.every((st) => stepStatus[st.key]);
  const activeStepKey = (BOOKING_STEPS.find((st) => !stepStatus[st.key]) || { key: "requirements" }).key;
  const prevStatusRef = useRef(stepStatus);

  // Auto-advance: the moment a step flips to complete, glide to the next unfinished section.
  useEffect(() => {
    const prev = prevStatusRef.current;
    prevStatusRef.current = stepStatus;
    const justDone = BOOKING_STEPS.find((st) => stepStatus[st.key] && !prev[st.key]);
    if (!justDone) return undefined;
    const next = BOOKING_STEPS.find((st) => !stepStatus[st.key]);
    const targetId = next ? next.id : "bk-pay";
    const t = setTimeout(() => {
      if (document.activeElement && ["INPUT", "TEXTAREA"].includes(document.activeElement.tagName) && next?.key === "requirements" && justDone.key === "contact") {
        document.activeElement.blur();
      }
      scrollToSection(bodyScrollRef, targetId, !!next);
    }, 650);
    return () => clearTimeout(t);
  }, [stepStatus.schedule, stepStatus.contact, stepStatus.requirements]);
  const allInBase = travelQuote?.status === "ok" ? travelQuote.final_price : (quote.total_price || 0) + equipmentFeeAmount;
  const totalWithPriority = allInBase + priorityFeeAmount;

  useEffect(() => {
    const addr = (bookingData.address || "").trim();
    if (addr.length < 8) { setTravelQuote(null); return undefined; }
    const t = setTimeout(async () => {
      setTravelLoading(true);
      try {
        const res = await axios.post(`${API}/quotes/${quote.id}/travel-estimate`, { address: addr });
        setTravelQuote(res.data);
      } catch (err) {
        logger.error("travel estimate failed", err);
        setTravelQuote(null);
      } finally {
        setTravelLoading(false);
      }
    }, 900);
    return () => clearTimeout(t);
  }, [bookingData.address, quote.id]);

  const checkAvailableTimeSlots = async (selectedDate) => {
    if (!selectedDate || !isDateAllowed(selectedDate)) {
      setBookedTimeSlots([]);
      return;
    }
    setCheckingAvailability(true);
    try {
      const res = await axios.get(`${API}/availability/${selectedDate}`);
      if (res.data.blocked_day) {
        toast.error(res.data.reason);
        setBookedTimeSlots([]);
        return;
      }
      setBookedTimeSlots(res.data.booked_slots || []);
      if (res.data.available_count === 0) {
        toast.warning("All time slots are booked for this date. Please choose another day.");
      }
    } catch (err) {
      logger.error("Failed to check availability:", err);
      setBookedTimeSlots([]);
    } finally {
      setCheckingAvailability(false);
    }
  };

  const handleDateChange = (selectedDate) => {
    if (!isDateAllowed(selectedDate)) {
      toast.error("Pickup is not available on weekends or Fridays. Please select Monday-Thursday.");
      return;
    }
    setBookingData({ ...bookingData, pickup_date: selectedDate, pickup_time: "" });
    checkAvailableTimeSlots(selectedDate);
    setShowCalendar(false);
  };

  const submitBooking = async (paymentChoice) => {
    // paymentChoice: "venmo" | "card" | "in_person"
    setFieldErrors({});
    const { errors, missing } = buildMissingFields(bookingData);

    if (missing.length > 0) {
      setFieldErrors(errors);
      toast.error(`Please complete the following required fields: ${missing.join(", ")}`);
      setTimeout(() => {
        const firstError = document.querySelector(".border-red-500");
        if (firstError) {
          firstError.scrollIntoView({ behavior: "smooth", block: "center" });
          firstError.focus();
        }
      }, 100);
      return;
    }

    if (!isDateAllowed(bookingData.pickup_date)) {
      toast.error("Selected date is not available for pickup");
      return;
    }
    if (bookedTimeSlots.includes(bookingData.pickup_time)) {
      toast.error("Selected time slot is already booked. Please choose another time.");
      return;
    }
    if (!legalConsent) {
      toast.error("Please agree to the Terms of Service and Refund Policy to continue.");
      return;
    }

    try {
      const res = await axios.post(`${API}/bookings`, {
        quote_id: quote.id,
        ...bookingData,
        priority_tier: priorityTier || null,
        consent_accepted: legalConsent,
        pay_in_person: paymentChoice === "in_person",
        payment_method: paymentChoice === "in_person" ? "cash" : paymentChoice === "callback" ? "venmo" : paymentChoice,
      });
      const bookingId = res.data.id;

      if (paymentChoice === "callback" || res.data.requires_manual_review) {
        toast.success("✅ Request received! We'll call you shortly to confirm a custom quote.", {
          duration: 5000,
          style: { background: "#10b981", color: "#ffffff", fontSize: "16px", fontWeight: "600", padding: "16px" },
        });
        setBookingSubmitted(true);
        setTimeout(() => {
          setBookingSubmitted(false);
          onSuccess();
        }, 6000);
        return;
      }

      if (quote.requires_approval) {
        setBookingSubmitted(true);
        setTimeout(() => {
          setBookingSubmitted(false);
          onSuccess();
        }, 7000);
        return;
      }

      // Pay-in-person customers skip the payment redirect
      if (paymentChoice === "in_person") {
        toast.success("✅ Booking confirmed! We'll see you on pickup day — please have cash or card ready.", {
          duration: 5000,
          style: { background: "#10b981", color: "#ffffff", fontSize: "16px", fontWeight: "600", padding: "16px" },
        });
        setBookingSubmitted(true);
        setTimeout(() => {
          setBookingSubmitted(false);
          onSuccess();
        }, 3500);
        return;
      }

      // Card → route to /pay/:id so the customer sees the Tip-the-Crew picker
      // BEFORE we kick off Stripe Checkout. The pay page has its own
      // "Pay with Card" button that creates the Stripe session (incl. tip).
      if (paymentChoice === "card") {
        toast.success("✅ Booking saved! Add an optional tip, then check out.", {
          duration: 3500,
          style: { background: "#10b981", color: "#ffffff", fontSize: "16px", fontWeight: "600", padding: "16px" },
        });
        window.location.href = `/pay/${bookingId}`;
        return;
      }

      // Venmo flow (default)
      toast.success("✅ Booking Successfully Submitted! Please complete payment to confirm.", {
        duration: 4000,
        style: { background: "#10b981", color: "#ffffff", fontSize: "16px", fontWeight: "600", padding: "16px" },
      });

      const venmoUrl = `https://venmo.com/code?user_id=Text2toss&amount=${totalWithPriority}&note=Text2toss%20Booking%20${bookingId.substring(0, 8)}`;
      const qrCodeDataUrl = await QRCode.toDataURL(venmoUrl, {
        width: 256,
        margin: 2,
        color: { dark: "#000000", light: "#FFFFFF" },
      });
      onVenmoPayment(bookingId, qrCodeDataUrl, totalWithPriority);
    } catch (err) {
      toast.error("Failed to create booking");
    }
  };

  const handleVenmoBooking = () => submitBooking(payInPerson ? "in_person" : "venmo");
  const handleCardBooking = () => submitBooking("card");

  if (bookingSubmitted) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-50 flex items-stretch sm:items-start justify-center sm:p-4 sm:pt-8 overflow-y-auto">
        <BookingSuccessScreen onClose={() => { setBookingSubmitted(false); onSuccess(); }} />
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-50 flex items-stretch sm:items-start justify-center sm:p-4 sm:pt-8">
      <Card className="w-full max-w-2xl shadow-2xl border-0 sm:mb-8 rounded-none sm:rounded-lg h-screen sm:h-auto sm:max-h-[calc(100vh-4rem)] flex flex-col">
        {/* Compact sticky header — title + price + step indicator on 2 rows */}
        <div className="sticky top-0 z-10 bg-black rounded-t-none sm:rounded-t-lg flex-shrink-0 border-b-2 border-cyan-400/30 relative overflow-hidden">
          <div className="absolute -top-12 -right-12 w-40 h-40 bg-cyan-400/15 rounded-full blur-3xl pointer-events-none"></div>
          <div className="relative px-4 py-2 flex items-center justify-between gap-3 border-b border-white/10">
            {/* Tappable step pills: Date → Contact → Pay */}
            <StepDots stepStatus={stepStatus} activeKey={activeStepKey} scrollRef={bodyScrollRef} />
            <button
              onClick={onClose}
              data-testid="modal-close-x"
              className="text-white/70 hover:text-white text-xl leading-none w-7 h-7 flex items-center justify-center rounded-full hover:bg-white/15 transition-colors"
              aria-label="Close"
            >
              ×
            </button>
          </div>
          <div className="relative px-4 py-3 flex items-center justify-between gap-3 text-white">
            <div className="min-w-0">
              <p className="text-[10px] font-display italic uppercase tracking-widest text-cyan-400 leading-none mb-1" data-testid="booking-price-label">
                {travelQuote?.status === "ok" ? "Your Text2Toss Quote" : "Complete Your Booking"}
              </p>
              {travelLoading && <p className="text-[10px] text-cyan-300 animate-pulse" data-testid="travel-quote-loading">Calculating travel to your address…</p>}
              {!travelLoading && travelQuote?.status === "manual_review" && (
                <p className="text-[10px] text-amber-300" data-testid="travel-quote-manual-review">We'll confirm your final price before pickup</p>
              )}
              {!travelLoading && travelQuote?.status === "out_of_area" && (
                <p className="text-[10px] text-amber-300" data-testid="travel-quote-out-of-area-hint">Outside our regular service area — see below</p>
              )}
              {!travelLoading && travelQuote?.status !== "manual_review" && (priorityTier || equipmentFeeAmount > 0) && (
                <p className="text-[10px] text-gray-400 truncate">
                  {priorityTier && <>+${priorityFeeAmount} priority</>}
                  {priorityTier && equipmentFeeAmount > 0 && travelQuote?.status !== "ok" && " · "}
                  {equipmentFeeAmount > 0 && travelQuote?.status !== "ok" && <>+${equipmentFeeAmount} equipment</>}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="font-display italic text-3xl text-white leading-none" data-testid="booking-total-price">{travelQuote?.status === "out_of_area" ? "Call us" : `$${totalWithPriority}`}</span>
              <Badge className="bg-cyan-400 text-black border-0 text-[10px] font-display italic uppercase px-2 py-0.5">💳 Venmo</Badge>
            </div>
          </div>
        </div>

        {/* Body — takes all remaining vertical space */}
        <div className="relative flex-1 min-h-0 flex flex-col">
        <div ref={bodyScrollRef} className="overflow-y-auto flex-1 min-h-0">
          <CardContent className="p-4 sm:p-6 space-y-6 sm:space-y-7">
            {quote.requires_approval && (
              <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-400 rounded-lg p-4 shadow-sm">
                <div className="flex items-start gap-3">
                  <span className="text-3xl">⏳</span>
                  <div>
                    <p className="text-base font-bold text-yellow-900 mb-2">Quote Pending Admin Approval</p>
                    <p className="text-sm text-yellow-800 mb-2">
                      Please fill out your booking information below. Your booking will be held as <strong>pending</strong> until your quote is approved.
                    </p>
                    <p className="text-xs text-yellow-700">
                      ✓ No payment will be processed until quote is approved<br />
                      ✓ We&apos;ll contact you within 24 hours with approval<br />
                      ✓ You can then complete payment to confirm your booking
                    </p>
                  </div>
                </div>
              </div>
            )}

            <section id="bk-schedule" className="transition-shadow">
            <SchedulePicker
              bookingData={bookingData}
              setBookingData={setBookingData}
              bookedTimeSlots={bookedTimeSlots}
              checkingAvailability={checkingAvailability}
              onOpenCalendar={() => setShowCalendar(true)}
            />

            <PriorityPicker
              value={priorityTier}
              onChange={onPriorityChange}
              pickupDate={bookingData.pickup_date}
            />
            </section>

            <section id="bk-contact" className="transition-shadow">
            <ContactFields
              bookingData={bookingData}
              setBookingData={setBookingData}
              fieldErrors={fieldErrors}
              setFieldErrors={setFieldErrors}
            />

            </section>

            <section id="bk-requirements" className="transition-shadow">
            <RequirementsSection
              bookingData={bookingData}
              setBookingData={setBookingData}
              fieldErrors={fieldErrors}
              setFieldErrors={setFieldErrors}
            />
            </section>

            <div id="bk-pay" className={`rounded-xl p-4 border-2 transition-colors ${
              payInPerson
                ? "bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-400"
                : "bg-gradient-to-br from-blue-50 to-blue-100 border-blue-300"
            }`}>
              <div className="text-center mb-3">
                <p className={`text-base font-bold mb-1 ${payInPerson ? "text-emerald-900" : "text-blue-900"}`}>
                  {payInPerson ? "💵 Pay In Person on Pickup Day" : "💰 Payment via Venmo"}
                </p>
                <p className={`text-sm ${payInPerson ? "text-emerald-800" : "text-blue-800"}`}>
                  {payInPerson
                    ? <>Have <strong>cash or card</strong> ready when the crew arrives. Your booking is locked in immediately.</>
                    : <>After booking, you&apos;ll get payment instructions for <strong>@Text2toss</strong></>}
                </p>
              </div>
              <label className="flex items-center justify-center gap-2 cursor-pointer bg-white/70 hover:bg-white rounded-lg py-2 px-3 border border-current/20">
                <input
                  type="checkbox"
                  checked={payInPerson}
                  onChange={(e) => setPayInPerson(e.target.checked)}
                  className="w-5 h-5 rounded border-2 border-gray-300 text-emerald-500 focus:ring-2 focus:ring-emerald-400 cursor-pointer"
                  data-testid="pay-in-person-checkbox"
                />
                <span className="text-sm font-semibold text-gray-800">
                  I&apos;ll pay in person (cash or card on pickup day)
                </span>
              </label>
            </div>
          </CardContent>
        </div>
        {formComplete && <ScrollHint scrollRef={bodyScrollRef} label="Scroll to review & pay" />}
        </div>

        {/* Sticky footer — guided "next step" until the form is complete, then consent + pay */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 rounded-b-lg">
          {!formComplete && travelQuote?.status !== "out_of_area" ? (
            <GuidedFooter bookingData={bookingData} scrollRef={bodyScrollRef} onCancel={onClose} />
          ) : (
          <>
          {/* Legal consent — required before submission */}
          <label className="flex items-start gap-3 px-4 pt-3 pb-2 cursor-pointer">
            <input
              type="checkbox"
              checked={legalConsent}
              onChange={(e) => setLegalConsent(e.target.checked)}
              className="mt-0.5 w-5 h-5 rounded border-2 border-gray-300 text-cyan-500 focus:ring-2 focus:ring-cyan-400 cursor-pointer flex-shrink-0"
              data-testid="legal-consent-checkbox"
            />
            <span className="text-xs text-gray-700 leading-snug">
              I agree to the{" "}
              <a
                href="/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="text-cyan-600 hover:text-cyan-700 font-semibold underline"
                data-testid="consent-terms-link"
              >
                Terms of Service
              </a>{" "}
              and{" "}
              <a
                href="/refund-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-cyan-600 hover:text-cyan-700 font-semibold underline"
                data-testid="consent-refund-link"
              >
                Refund Policy
              </a>
              . I understand that the price shown is an AI-generated estimate and may be adjusted at pickup based on actual volume.
            </span>
          </label>

          <div className="p-4 pt-2 pb-20 sm:pb-4 flex flex-col gap-3">
            {travelQuote?.status === "out_of_area" ? (
              // Beyond the service radius — no automatic price; "call us" instead
              <>
                <div data-testid="out-of-area-card" className="rounded-xl border-2 border-amber-300 bg-amber-50 p-4 text-center space-y-2">
                  <p className="text-2xl">📞</p>
                  <p className="font-display italic uppercase tracking-wider text-amber-900">Let's talk about your pickup</p>
                  <p className="text-sm text-amber-900" data-testid="out-of-area-message">{travelQuote.message}</p>
                  <a
                    href={`tel:${(travelQuote.message.match(/\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}/) || ["9288539619"])[0].replace(/\D/g, "")}`}
                    data-testid="out-of-area-call-btn"
                    className="inline-flex items-center justify-center w-full h-12 rounded-xl bg-black text-cyan-400 border-2 border-cyan-400 font-display italic uppercase tracking-wider text-base"
                  >
                    📞 Call Text2Toss
                  </a>
                </div>
                <Button
                  onClick={() => submitBooking("callback")}
                  disabled={!legalConsent}
                  data-testid="out-of-area-request-btn"
                  variant="outline"
                  className="w-full h-11 border-2 border-amber-300 text-amber-900 hover:bg-amber-100 text-sm font-semibold disabled:opacity-50"
                >
                  Or request a custom quote — we'll call you
                </Button>
                <Button variant="outline" onClick={onClose} data-testid="cancel-booking-btn" className="w-full h-11 border-2 text-sm font-semibold">
                  Cancel
                </Button>
              </>
            ) : quote.requires_approval ? (
              <>
                <Button
                  onClick={handleVenmoBooking}
                  disabled={!legalConsent}
                  data-testid="venmo-booking-btn"
                  className="w-full h-12 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white text-base font-bold shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  📝 Submit Booking (Pending Approval)
                </Button>
                <Button
                  variant="outline"
                  onClick={onClose}
                  data-testid="cancel-booking-btn"
                  className="w-full h-11 border-2 text-base font-semibold"
                >
                  Cancel
                </Button>
              </>
            ) : payInPerson ? (
              // Pay-in-person → single confirm button (no payment redirect)
              <>
                <Button
                  onClick={handleVenmoBooking}
                  disabled={!legalConsent}
                  data-testid="venmo-booking-btn"
                  className="w-full h-12 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white text-base font-bold shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  💵 Confirm Booking (Pay on Pickup)
                </Button>
                <Button
                  variant="outline"
                  onClick={onClose}
                  data-testid="cancel-booking-btn"
                  className="w-full h-11 border-2 text-base font-semibold"
                >
                  Cancel
                </Button>
              </>
            ) : (
              // Standard checkout — two payment buttons stacked
              <>
                <Button
                  onClick={handleCardBooking}
                  disabled={!legalConsent}
                  data-testid="card-booking-btn"
                  className="w-full h-12 bg-cyan-400 hover:bg-cyan-300 text-black font-display italic uppercase tracking-wider text-base shadow-[0_4px_14px_-2px_rgba(34, 211, 238,0.5)] rounded-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
                >
                  💳 Pay with Card · ${totalWithPriority}
                </Button>
                <Button
                  onClick={handleVenmoBooking}
                  disabled={!legalConsent}
                  data-testid="venmo-booking-btn"
                  className="w-full h-12 bg-black hover:bg-gray-900 text-cyan-400 border-2 border-cyan-400 font-display italic uppercase tracking-wider text-base rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  📱 Pay with Venmo · ${totalWithPriority}
                </Button>
                <Button
                  variant="outline"
                  onClick={onClose}
                  data-testid="cancel-booking-btn"
                  className="w-full h-11 border-2 text-sm font-semibold"
                >
                  Cancel
                </Button>
              </>
            )}
          </div>
          </>
          )}
        </div>
      </Card>

      {showCalendar && (
        <AvailabilityCalendar
          selectedDate={bookingData.pickup_date}
          onDateSelect={handleDateChange}
          onClose={() => setShowCalendar(false)}
        />
      )}
    </div>
  );
};

export default BookingModal;
